import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/*
  To / Cc / Bcc for an auto-schedule (Digest's digest_schedule, Reports'
  report_schedules) — three free-text email chip lists, unlike
  RecipientFields.jsx's To/Cc/Bcc which draw "To" from a fixed pool (owners,
  a project's manager). A schedule has no such pool to offer: it is edited
  from a plain settings panel days or weeks before anything is generated, so
  every field here is typed, not picked.

  Cc/Bcc are collapsed behind "+ Add Cc"/"+ Add Bcc" by default, the same
  expand/retract pattern RecipientFields already uses on the manual Send
  panel — most schedules only ever need a To list, so two permanently-open
  empty fields were pure clutter. Whichever already HAS addresses saved opens
  automatically (whether that's on first load or right after a save), so
  nothing already configured is ever hidden from view.
*/

const EMAIL = /^\S+@\S+\.\S+$/;

function ChipRow({ label, field, value, onChange, testId, emptyHint, teamEmails }) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const val = draft.trim().replace(/,$/, "");
    if (!val) return;
    if (!EMAIL.test(val)) { toast.error("Invalid email"); return; }
    if (value.includes(val)) { setDraft(""); return; }
    onChange([...value, val]);
    setDraft("");
  };

  // Only passed for the digest's To field — a portfolio-wide weekly digest
  // going to "everyone" is the normal case, unlike a per-project/member
  // report, which should stay scoped to the people it's actually about.
  const missing = (teamEmails || []).filter((e) => !value.includes(e));

  return (
    <div className="mb-2">
      <Label className="trk-uppercase-label text-[10px] mb-1 block">
        {label}{value.length > 0 ? ` (${value.length})` : ""}
      </Label>
      <div className="flex flex-wrap gap-1 mb-1.5 min-h-[22px]">
        {value.map((r) => (
          <span key={r} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-zinc-50 border border-border rounded-sm">
            {r}
            <button
              type="button"
              onClick={() => onChange(value.filter((x) => x !== r))}
              data-testid={`${testId}-${field}-remove-${r}`}
              className="hover:text-red-600"
            >
              <XIcon size={10} weight="bold" />
            </button>
          </span>
        ))}
        {value.length === 0 && <span className="text-[10px] font-mono text-zinc-400">{emptyHint}</span>}
      </div>
      <div className="flex items-center gap-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="email@company.com"
          className="rounded-sm h-8 text-xs"
          data-testid={`${testId}-${field}-input`}
        />
        <Button size="sm" variant="outline" onClick={add} className="rounded-sm h-8 px-2 text-xs" data-testid={`${testId}-${field}-add`}>
          Add
        </Button>
      </div>
      {teamEmails && missing.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([...value, ...missing])}
          data-testid={`${testId}-${field}-add-team`}
          className="text-[10px] font-mono uppercase tracking-wider text-[#0176D3] hover:underline mt-1"
        >
          + Whole team ({teamEmails.length})
        </button>
      )}
    </div>
  );
}

/**
 * value: { recipients, cc, bcc } (the same field names digest_schedule and
 * report_schedules already use — "recipients" for To, not "to", to match
 * the existing save payload both pages send).
 * onChange(patch) is called with just the changed field, e.g. { cc: [...] }
 * — callers merge it into the current schedule and save, the same way the
 * day/hour selects already do.
 *
 * Render with a `key` that changes per schedule (e.g. the report's
 * scope+target) when more than one schedule can appear in the same spot —
 * ReportsPage does this — so switching targets re-evaluates which of Cc/Bcc
 * should start open instead of carrying over the previous target's state.
 */
export default function ScheduleRecipientFields({ value, onChange, testId = "schedule", teamEmails }) {
  const recipients = value?.recipients || [];
  const cc = value?.cc || [];
  const bcc = value?.bcc || [];

  const [showCc, setShowCc] = useState(cc.length > 0);
  const [showBcc, setShowBcc] = useState(bcc.length > 0);

  // Covers the schedule arriving AFTER mount (an async fetch resolving, or a
  // save round-trip) — whatever already has addresses in it is revealed,
  // never left collapsed just because the request hadn't returned yet.
  useEffect(() => { if (cc.length > 0) setShowCc(true); }, [cc.length]);
  useEffect(() => { if (bcc.length > 0) setShowBcc(true); }, [bcc.length]);

  return (
    <div>
      <ChipRow
        label="To" field="recipients" value={recipients}
        onChange={(next) => onChange({ recipients: next })}
        testId={testId} emptyHint="Empty — job will skip" teamEmails={teamEmails}
      />

      {showCc && (
        <ChipRow
          label="Cc" field="cc" value={cc}
          onChange={(next) => onChange({ cc: next })}
          testId={testId} emptyHint="None"
        />
      )}

      {showBcc && (
        <ChipRow
          label="Bcc" field="bcc" value={bcc}
          onChange={(next) => onChange({ bcc: next })}
          testId={testId} emptyHint="None"
        />
      )}

      <div className="flex items-center gap-3 mb-2">
        {!showCc && (
          <button
            type="button"
            onClick={() => setShowCc(true)}
            data-testid={`${testId}-show-cc`}
            className="text-[10px] font-mono uppercase tracking-wider text-[#0176D3] hover:underline"
          >
            + Add Cc
          </button>
        )}
        {!showBcc && (
          <button
            type="button"
            onClick={() => setShowBcc(true)}
            data-testid={`${testId}-show-bcc`}
            className="text-[10px] font-mono uppercase tracking-wider text-[#0176D3] hover:underline"
          >
            + Add Bcc
          </button>
        )}
      </div>
    </div>
  );
}
